import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
/* THE TWO SCREENS THAT NEVER GOT A DARK MODE (2026-09-01, owner report + screenshot).
 *
 * These were the ONLY two files in `client/src` still importing `colors` — the FROZEN LIGHT
 * palette — rather than `themeVar`, which is the same key set emitted as `var(--vb-color-*, <light
 * fallback>)`. Measured, not guessed: 37 components import `themeVar`; `BriefsScreen` and
 * `PropertyTimelineScreen` imported `colors`. A hex compiled into an inline style cannot respond to
 * `prefers-color-scheme` or to `[data-theme]`, so every surface here stayed light-mode while the
 * page around it went dark — which is why the table header rendered as a near-white strip on a dark
 * ground and the address links sat at the light palette's slate on it.
 *
 * The swap is safe by construction: `themeVar` is typed `Record<keyof typeof colors, string>`, so
 * every name resolves or `tsc` fails, and each var carries its own light hex as the fallback — a
 * surface that somehow renders outside `app.css` still gets a real colour rather than `inherit`.
 */
import { themeVar as colors } from "../design-tokens";
import { AppShell } from "../ui/AppShell";
import { useDocumentTitle, vbTitle } from "../ui/useDocumentTitle";
import { ApiError, deleteBrief, duplicateBrief, listBriefs, restoreBrief } from "./api";
import {
  DASH,
  briefTitle,
  kindShort,
  missingFigureReason,
  money,
  shortDate,
  statusLabel,
} from "./format";
import type { BriefRow } from "./types";
import { blockStyle } from "../ui/Block";
/* THE SAME CHOOSER THE APP HOME USES (S03, 2026-09-22) — one component, two call sites. Two
   copies of it would be two places for the three verticals to fall out of step, and their
   co-equality is the one thing about them that may not drift. */
import { NewBriefChooser } from "../home/NewBriefChooser";
import { APPRAISAL_SUPPORT_ENABLED } from "../flags";
import { useSession } from "../lib/useSession";
import { useModuleStatus } from "../lib/useModuleStatus";

/**
 * YOUR BRIEFS — `/briefs`.
 *
 * THE GAP THIS CLOSES. Every finished brief has always lived at a stable, reloadable URL
 * (`/cma/ambj_...`) and **nothing has ever listed it**. The server offered `GET /api/amb/jobs/{id}`
 * — fetch one, if you already knew its id — and no list route at all. So an agent who ran a brief
 * on Tuesday and closed the tab had no route back to their own work at any layer: not a screen, not
 * an endpoint, not a query. The only recovery was a URL in browser history. This is that route.
 *
 * WHAT IS AND IS NOT A SOURCE HERE
 * --------------------------------
 * The figure in each row is a CACHED DISPLAY COPY (`headline_value` on the registry). It has no
 * provenance chip because it has no provenance to show — it is a copy kept so that fifty rows are
 * one query instead of fifty. It is deliberately rendered in the secondary weight, and every row's
 * primary action opens the report, where the same number carries its chip and its drawer. Nothing
 * on this screen is a number a client should be shown. That is why there is no "export this list"
 * button and no total at the bottom: a total of cached copies would be a figure with no source, and
 * this product does not ship those.
 *
 * A missing figure prints an em dash with a reason, never `$0`. A queued or failed brief is a real
 * row — it happened — and it says so rather than reading as a valuation of nothing.
 *
 * DELETE IS SOFT, AND SAYS SO
 * ---------------------------
 * Removing a brief takes it off this list and leaves the report itself untouched at its URL. A
 * brief may already have been handed to a client and cited in a hearing; a list that could erase
 * our own record of a document that exists in the world would be worse than no list. The undo is
 * inline and the deleted rows stay reachable behind a filter, not in a hidden graveyard.
 */

type Filter = "all" | "mine" | "deleted";

const PAGE_SIZE = 25;

export function BriefsScreen() {
  useDocumentTitle(vbTitle("Your briefs"));

  const [rows, setRows] = useState<BriefRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [query, setQuery] = useState("");
  const [offset, setOffset] = useState(0);
  const [chooserOpen, setChooserOpen] = useState(false);

  /** The last delete, so it can be undone from a live control rather than a timed toast. A toast
   * that expires takes the undo with it, and this action removes something a person may have spent
   * an hour on. */
  const [undo, setUndo] = useState<{ row: BriefRow; label: string } | null>(null);
  /** The copy just made, and the way into it. Cleared when the reader acts or dismisses. */
  const [copied, setCopied] = useState<{ open: string; label: string } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const search = useDebounced(query, 250);

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      listBriefs({
        mine: filter === "mine",
        includeDeleted: filter === "deleted",
        q: search || undefined,
        limit: PAGE_SIZE,
        offset,
        signal,
      })
        .then((page) => {
          // The `deleted` filter asks the server for BOTH and narrows here, because the API's
          // `include_deleted` widens rather than replaces — there is no "only deleted" mode, and
          // inventing one in a query parameter the server does not have would 422.
          const items =
            filter === "deleted" ? page.items.filter((r) => r.deleted_at) : page.items;
          setRows(items);
          setTotal(filter === "deleted" ? items.length : page.count);
          setError(null);
        })
        .catch((e) => {
          if (signal?.aborted) return;
          setError(messageFor(e));
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [filter, search, offset],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  async function handleDelete(row: BriefRow) {
    setBusyId(row.id);
    try {
      await deleteBrief(row.id);
      setUndo({ row, label: briefTitle(row).text });
      setRows((current) => current.filter((r) => r.id !== row.id));
      setTotal((n) => Math.max(0, n - 1));
      setError(null);
    } catch (e) {
      setError(messageFor(e));
    } finally {
      setBusyId(null);
    }
  }

  /**
   * ================================================================================================
   * DUPLICATE (F1, 2026-09-21) — owner request: "so if an agent wants to duplicate without making
   * changes to the brief they can."
   * ================================================================================================
   * IT DOES NOT NAVIGATE. Copying a brief and being thrown into the copy takes the decision away
   * from the person who made it: they may want to copy three and then open one. The list reloads so
   * the copy is there, and a bar offers the way in — the same shape the delete/undo bar already
   * uses, because this is the same kind of event.
   *
   * `row.source_id`, NOT `row.id`. The shelf row and the job are different identifiers for
   * different things; this acts on the job.
   */
  async function handleDuplicate(row: BriefRow) {
    setBusyId(row.id);
    try {
      const made = await duplicateBrief(row.source_id);
      setCopied({ open: made.open, label: briefTitle(row).text });
      setError(null);
      await load();
    } catch (e) {
      /* THE ONE FAILURE WORTH ITS OWN SENTENCE. The route is scoped to the job's owner, exactly as
         `/editable` and `/result` are, while this list is scoped to the ORG — so a colleague can
         see a brief their firm paid for and cannot copy it. Saying "not found" there would be
         true of the API and useless to the reader.

         THE STATUS IS READ OFF THE ERROR, NOT MATCHED BY CLASS, AND A TEST IS WHY. There are two
         `ApiError`s in this client — the shelf's in `crm/api` and `lib/auth`'s — and `duplicate`
         goes through `apiFetch`, so it throws the SECOND one. `instanceof` against the first was
         silently false and the reader got the API's own "job not found". One of the two would
         always have been the wrong guess; the status is the thing both of them carry. */
      setError(
        statusOf(e) === 404
          ? "That brief belongs to a colleague. Only the person who created a brief can copy it — " +
            "ask them to make the copy, or open it and start a new analysis from the same address."
          : messageFor(e),
      );
    } finally {
      setBusyId(null);
    }
  }

  async function handleRestore(reportId: string) {
    setBusyId(reportId);
    try {
      await restoreBrief(reportId);
      setUndo(null);
      load();
    } catch (e) {
      setError(messageFor(e));
    } finally {
      setBusyId(null);
    }
  }

  const showingRange = useMemo(() => {
    if (!rows.length) return null;
    const first = offset + 1;
    return `${first}${DASH}${offset + rows.length} of ${total}`;
  }, [offset, rows.length, total]);

  return (
    <AppShell step="briefs" content="form">
      <div style={{ padding: "8px 0 48px" }}>
        {/* THE HEADING AND THE ACTION ON ONE BASELINE. `flexWrap` because at 390px "Your briefs"
            and "New brief" do not share a line, and without it the button would be squeezed below
            its intrinsic width — the same defect `ui/AppShell`'s bar had. */}
        <header
          style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 20 }}
        >
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 600, margin: "0 0 4px" }}>Your briefs</h1>
            <p style={{ fontSize: 14, color: colors.textSecondary, margin: 0, ...blockStyle("copy") }}>
              Everything your organization has generated. Open one to see its figures with their
              sources; the values below are a cached preview and are not sourced.
            </p>
          </div>
          {/* S03: the shelf is where somebody realises they need another brief, and until now the
              only way on from here was the bar's primary action — which skipped the chooser and so
              never shows that the same brief serves all three verticals. */}
          <button type="button" className="btn p" style={{ flex: "none" }} onClick={() => setChooserOpen(true)}>
            New brief
          </button>
        </header>

        <Toolbar
          filter={filter}
          onFilter={(next) => {
            setFilter(next);
            setOffset(0);
          }}
          query={query}
          onQuery={(next) => {
            setQuery(next);
            setOffset(0);
          }}
        />

        {undo && (
          <UndoBar
            label={undo.label}
            busy={busyId === undo.row.id}
            onUndo={() => handleRestore(undo.row.id)}
            onDismiss={() => setUndo(null)}
          />
        )}

        {copied && (
          <CopiedBar
            label={copied.label}
            open={copied.open}
            onDismiss={() => setCopied(null)}
          />
        )}

        {error && <ErrorNote message={error} onRetry={() => load()} />}

        {loading && !rows.length && (
          <p style={{ fontSize: 14, color: colors.textSecondary }}>Loading your briefs…</p>
        )}

        {!loading && !rows.length && !error && <EmptyState filter={filter} hasQuery={!!search} />}

        {!!rows.length && (
          <>
            <BriefTable
              rows={rows}
              busyId={busyId}
              onDelete={handleDelete}
              onRestore={handleRestore}
              onDuplicate={handleDuplicate}
            />
            <footer
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                marginTop: 16,
                fontSize: 13,
                color: colors.textSecondary,
              }}
            >
              <span>{showingRange}</span>
              <span style={{ display: "flex", gap: 8 }}>
                <PagerButton
                  disabled={offset === 0}
                  onClick={() => setOffset((n) => Math.max(0, n - PAGE_SIZE))}
                >
                  Previous
                </PagerButton>
                <PagerButton
                  disabled={offset + rows.length >= total}
                  onClick={() => setOffset((n) => n + PAGE_SIZE)}
                >
                  Next
                </PagerButton>
              </span>
            </footer>
          </>
        )}
      </div>

      <NewBriefChooser open={chooserOpen} onClose={() => setChooserOpen(false)} />
    </AppShell>
  );
}

/* --------------------------------------------------------------------------- *
 * The table                                                                    *
 * --------------------------------------------------------------------------- */

function BriefTable({
  rows,
  busyId,
  onDelete,
  onRestore,
  onDuplicate,
}: {
  rows: BriefRow[];
  busyId: string | null;
  onDelete: (row: BriefRow) => void;
  onRestore: (id: string) => void;
  onDuplicate: (row: BriefRow) => void;
}) {
  // One `/api/me` for the whole table, not one per row: the appraisal link below is offered only
  // on the signed-in user's own briefs (S10).
  const session = useSession();
  const { crmEnabled } = useModuleStatus();
  return (
    <div style={{ overflowX: "auto", border: `1px solid ${colors.border}`, borderRadius: 10 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, minWidth: 720 }}>
        <caption className="vb-sr-only">
          Briefs your organization has generated, newest first. The value column is a cached preview
          and is not a sourced figure.
        </caption>
        <thead>
          <tr>
            <Th>Property</Th>
            <Th>Type</Th>
            <Th>Created</Th>
            <Th align="right">Indicated value</Th>
            <Th>Status</Th>
            <Th align="right">
              <span className="vb-sr-only">Actions</span>
            </Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <BriefRowView
              key={row.id}
              row={row}
              busy={busyId === row.id}
              onDelete={() => onDelete(row)}
              onRestore={() => onRestore(row.id)}
              onDuplicate={() => onDuplicate(row)}
              myUserId={session.id}
              crmEnabled={crmEnabled}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BriefRowView({
  row,
  busy,
  onDelete,
  onRestore,
  onDuplicate,
  myUserId = null,
  crmEnabled = false,
}: {
  row: BriefRow;
  busy: boolean;
  onDelete: () => void;
  onRestore: () => void;
  onDuplicate: () => void;
  myUserId?: string | null;
  /** The CRM link is offered only where it opens (`/api/next/crm/status` says the CRM is on). */
  crmEnabled?: boolean;
}) {
  const name = briefTitle(row);
  const value = money(row.headline_value);
  const created = shortDate(row.generated_at ?? row.created_at);
  const deleted = !!row.deleted_at;

  return (
    <tr style={{ borderTop: `1px solid ${colors.border}`, opacity: deleted ? 0.6 : 1 }}>
      <Td>
        {row.view_path && !deleted ? (
          <a
            href={row.view_path}
            style={{ color: colors.accent, fontWeight: 500, textDecoration: "none" }}
          >
            {name.text}
          </a>
        ) : (
          <span style={{ fontWeight: 500, color: name.isPlaceholder ? colors.textSecondary : undefined }}>
            {name.text}
          </span>
        )}
        {/* The timeline link is the "go back in time" entry point, and it only exists when the
            engine gave this brief a property key — an address it could not normalise cannot be
            grouped with anything, and a link that silently showed one report as a "history" would
            be a worse answer than no link. */}
        {row.property_key && (
          <>
            {" "}
            <Link
              to={`/briefs/property/${encodeURIComponent(row.property_key)}`}
              style={{ fontSize: 12, color: colors.textSecondary }}
            >
              history
            </Link>
          </>
        )}
        {/* "OPEN IN APPRAISAL VIEW" (S10, 2026-09-23) — a brief built by `/cma` read as an appraiser's
            workfile. OFFERED ONLY WHERE IT OPENS: this shelf lists the whole organization's briefs,
            and the appraisal view is owner-only (`appraisal_next`, `user_id == caller`), so a link on
            a colleague's brief would be a 404 — a dead control — and so would an unfinished one (409).
            `source_id` is the `AmbJob.id` (`report_sync.py:131`). A brief that kept no structured
            inputs still opens, to the server's own sentence saying why it cannot be drawn. */}
        {APPRAISAL_SUPPORT_ENABLED && !deleted && row.source_table === "amb_jobs" &&
          row.status === "done" && !!myUserId && row.owner_user_id === myUserId && (
          <>
            {" "}
            <Link
              to={`/appraisal/${encodeURIComponent(row.source_id)}`}
              data-testid="open-in-appraisal-view"
              style={{ fontSize: 12, color: colors.textSecondary }}
            >
              appraisal view
            </Link>
          </>
        )}
        {/* "IN THE CRM" (2026-10-18). This shelf and the CRM's Proposals & reports list the same
            rows, and only the CRM knows what a brief is filed against. The link opens that row with
            its filings showing, each one a way to its parcel or engagement. Offered only when the
            CRM is on, so it never leads to a route that 404s. */}
        {crmEnabled && !deleted && (
          <>
            {" "}
            <Link
              to={`/crm/briefs?report=${encodeURIComponent(row.id)}`}
              data-testid="open-in-crm"
              style={{ fontSize: 12, color: colors.textSecondary }}
            >
              in the CRM
            </Link>
          </>
        )}
        {!row.view_path && row.view_note && (
          <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 2 }}>
            {DASH} {row.view_note}
          </div>
        )}
      </Td>
      <Td>
        <Chip>{kindShort(row.kind)}</Chip>
      </Td>
      <Td muted>{created ?? <Unknown reason="no date recorded" />}</Td>
      <Td align="right">
        {value ? (
          // Secondary weight ON PURPOSE. This is a cached copy with no provenance; it must not
          // look like the sourced figure on the report screen, which is the one a client reads.
          <span style={{ color: colors.textSecondary, fontVariantNumeric: "tabular-nums" }}>
            {value}
          </span>
        ) : (
          <Unknown reason={missingFigureReason(row.status)} />
        )}
      </Td>
      <Td>
        <StatusPill status={row.status} deleted={deleted} />
      </Td>
      <Td align="right">
        {deleted ? (
          <RowButton onClick={onRestore} disabled={busy}>
            Restore
          </RowButton>
        ) : (
          <>
            {/* OFFERED ONLY WHERE IT CAN WORK. `duplicate` copies a finished payload from an
                `amb_jobs` row; a protest analysis lives in another store and a brief that has not
                finished has no payload to copy. Rather than showing a control that explains itself
                after the click, the cases that cannot work do not draw one. */}
            {canDuplicate(row) && (
              <RowButton onClick={onDuplicate} disabled={busy}>
                Duplicate
              </RowButton>
            )}{" "}
            <RowButton onClick={onDelete} disabled={busy} danger>
              Remove
            </RowButton>
          </>
        )}
      </Td>
    </tr>
  );
}

/**
 * Can this row be copied?
 *
 * THREE CONDITIONS, EACH FOR A REASON THE SERVER WOULD OTHERWISE HAVE TO EXPLAIN AFTER THE CLICK:
 * the payload lives on an `amb_jobs` row (a protest analysis is in another store and has no
 * duplicate route), the brief is finished (a partial payload copied is a brief that looks finished
 * and is not — the route answers 409), and it is not in the bin.
 *
 * OWNERSHIP IS NOT CHECKED HERE, and that is deliberate rather than forgotten. This list is
 * ORG-scoped so a colleague can see a brief their firm paid for, and the row does not say who the
 * reader is — only who owns it. Guessing would hide the button from the one person it works for.
 * The 404 that a colleague gets is answered with a sentence that says what to do instead.
 */
/**
 * The HTTP status an API error carries, whichever of this client's two `ApiError` classes it is,
 * or null for anything else. Structural on purpose — see `handleDuplicate` for what matching by
 * class cost.
 */
function statusOf(e: unknown): number | null {
  const status = (e as { status?: unknown } | null)?.status;
  return typeof status === "number" ? status : null;
}

function canDuplicate(row: BriefRow): boolean {
  return row.source_table === "amb_jobs" && row.status === "done" && !row.deleted_at;
}

/**
 * WHAT JUST HAPPENED, AND THE WAY INTO IT.
 *
 * Deliberately not a redirect: copying three briefs and being thrown into the third is not what
 * anybody meant. It mirrors `UndoBar` because it is the same kind of event — something happened to
 * the list, here is the one thing you probably want to do about it.
 */
function CopiedBar({
  label,
  open,
  onDismiss,
}: {
  label: string;
  open: string;
  onDismiss: () => void;
}) {
  return (
    <div
      role="status"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 12px",
        marginBottom: 12,
        borderLeft: `2px solid ${colors.accent}`,
        background: colors.bgSurface,
        fontSize: 14,
      }}
    >
      <span style={{ flex: 1 }}>
        Copied <strong>{label}</strong>. The original is unchanged.
      </span>
      <a href={open} style={{ color: colors.accent, fontWeight: 500, textDecoration: "none" }}>
        Open the copy
      </a>
      <RowButton onClick={onDismiss}>Dismiss</RowButton>
    </div>
  );
}

/* --------------------------------------------------------------------------- *
 * Small parts                                                                  *
 * --------------------------------------------------------------------------- */

function Toolbar({
  filter,
  onFilter,
  query,
  onQuery,
}: {
  filter: Filter;
  onFilter: (next: Filter) => void;
  query: string;
  onQuery: (next: string) => void;
}) {
  const tabs: { id: Filter; label: string }[] = [
    { id: "all", label: "All briefs" },
    { id: "mine", label: "Mine" },
    { id: "deleted", label: "Removed" },
  ];
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 12,
        marginBottom: 16,
      }}
    >
      <div role="tablist" aria-label="Filter briefs" style={{ display: "flex", gap: 4 }}>
        {tabs.map((tab) => {
          const active = filter === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              data-vb-hit="44"
              onClick={() => onFilter(tab.id)}
              style={{
                padding: "7px 12px",
                fontSize: 13,
                fontWeight: active ? 600 : 400,
                color: active ? colors.accent : colors.textSecondary,
                background: active ? colors.accentSoft : "transparent",
                border: `1px solid ${active ? colors.accentSoft : "transparent"}`,
                borderRadius: 8,
                cursor: "pointer",
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      <label style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
        <span className="vb-sr-only">Search briefs by address</span>
        <input
          type="search"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="Search by address"
          style={{
            width: 240,
            maxWidth: "100%",
            padding: "8px 10px",
            fontSize: 14,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            background: colors.bgSurface,
            color: colors.textPrimary,
          }}
        />
      </label>
    </div>
  );
}

function UndoBar({
  label,
  busy,
  onUndo,
  onDismiss,
}: {
  label: string;
  busy: boolean;
  onUndo: () => void;
  onDismiss: () => void;
}) {
  return (
    <div
      role="status"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
        padding: "10px 14px",
        marginBottom: 16,
        border: `1px solid ${colors.border}`,
        borderRadius: 10,
        background: colors.bgSurface,
        fontSize: 14,
      }}
    >
      <span>
        Removed <strong style={{ fontWeight: 600 }}>{label}</strong> from your list. The report
        itself is untouched.
      </span>
      <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
        <RowButton onClick={onUndo} disabled={busy}>
          Undo
        </RowButton>
        <RowButton onClick={onDismiss} disabled={busy}>
          Dismiss
        </RowButton>
      </span>
    </div>
  );
}

function EmptyState({ filter, hasQuery }: { filter: Filter; hasQuery: boolean }) {
  if (hasQuery) {
    return (
      <p style={{ fontSize: 14, color: colors.textSecondary }}>
        No briefs match that address. Searching looks at the address on the brief, not inside it.
      </p>
    );
  }
  if (filter === "deleted") {
    return (
      <p style={{ fontSize: 14, color: colors.textSecondary }}>
        Nothing removed. Briefs you remove appear here and can be restored.
      </p>
    );
  }
  if (filter === "mine") {
    return (
      <p style={{ fontSize: 14, color: colors.textSecondary }}>
        You haven't generated a brief yet. Briefs your colleagues made are under{" "}
        <strong style={{ fontWeight: 600 }}>All briefs</strong>.
      </p>
    );
  }
  // The first-run state. A prompt to do the thing, not an empty table with headers over nothing.
  return (
    <div
      style={{
        border: `1px solid ${colors.border}`,
        borderRadius: 10,
        padding: "28px 24px",
        background: colors.bgSurface,
        ...blockStyle("copy"),
      }}
    >
      <h2 style={{ fontSize: 16, fontWeight: 600, margin: "0 0 6px" }}>No briefs yet</h2>
      <p style={{ fontSize: 14, color: colors.textSecondary, margin: "0 0 16px" }}>
        Every brief you generate lands here automatically, with the property, the date and a link
        back to the full report. Nothing to set up.
      </p>
      <a
        href="/cma"
        style={{
          display: "inline-block",
          padding: "10px 16px",
          fontSize: 14,
          fontWeight: 600,
          color: "#FFFFFF",
          background: colors.accent,
          borderRadius: 8,
          textDecoration: "none",
        }}
      >
        Start an analysis
      </a>
    </div>
  );
}

function ErrorNote({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div
      role="alert"
      style={{
        padding: "10px 14px",
        marginBottom: 16,
        border: `1px solid ${colors.border}`,
        borderLeft: `3px solid ${colors.caution}`,
        borderRadius: 8,
        background: colors.cautionBg,
        fontSize: 14,
        color: colors.textPrimary,
      }}
    >
      {message}{" "}
      <button
        type="button"
        onClick={onRetry}
        data-vb-hit="44"
        style={{
          border: "none",
          background: "none",
          padding: 0,
          color: colors.accent,
          textDecoration: "underline",
          cursor: "pointer",
          fontSize: 14,
        }}
      >
        Try again
      </button>
    </div>
  );
}

function StatusPill({ status, deleted }: { status: string | null; deleted: boolean }) {
  if (deleted) {
    return <Chip tone="muted">Removed</Chip>;
  }
  const label = statusLabel(status);
  if (!label) return <Unknown reason="no status recorded" />;
  if (status === "done") return <Chip tone="good">{label}</Chip>;
  if (status === "failed" || status === "error") return <Chip tone="caution">{label}</Chip>;
  return <Chip tone="muted">{label}</Chip>;
}

function Chip({
  children,
  tone = "neutral",
}: {
  children: React.ReactNode;
  tone?: "neutral" | "good" | "caution" | "muted";
}) {
  const palette = {
    neutral: { fg: colors.accent, bg: colors.neutralChip },
    good: { fg: colors.verified, bg: colors.verifiedBg },
    caution: { fg: colors.caution, bg: colors.cautionBg },
    muted: { fg: colors.textSecondary, bg: colors.bgApp },
  }[tone];
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        fontSize: 12,
        fontWeight: 500,
        color: palette.fg,
        background: palette.bg,
        borderRadius: 6,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

/**
 * An em dash and the reason it is one. Never `$0`, never an empty cell — a blank reads as "we
 * checked and found nothing", which is a different claim from "we do not know".
 */
function Unknown({ reason }: { reason: string }) {
  return (
    <span style={{ color: colors.textSecondary }} title={reason}>
      {DASH} <span style={{ fontSize: 12 }}>{reason}</span>
    </span>
  );
}

function Th({ children, align = "left" }: { children: React.ReactNode; align?: "left" | "right" }) {
  return (
    <th
      scope="col"
      style={{
        textAlign: align,
        padding: "10px 14px",
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: "0.02em",
        textTransform: "uppercase",
        color: colors.textSecondary,
        background: colors.bgApp,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = "left",
  muted = false,
}: {
  children: React.ReactNode;
  align?: "left" | "right";
  muted?: boolean;
}) {
  return (
    <td
      style={{
        textAlign: align,
        padding: "12px 14px",
        verticalAlign: "top",
        color: muted ? colors.textSecondary : colors.textPrimary,
      }}
    >
      {children}
    </td>
  );
}

function RowButton({
  children,
  onClick,
  disabled,
  danger = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-vb-hit="44"
      style={{
        padding: "6px 10px",
        fontSize: 13,
        color: danger ? colors.caution : colors.accent,
        background: colors.bgSurface,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}

function PagerButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <RowButton onClick={onClick} disabled={disabled}>
      {children}
    </RowButton>
  );
}

/* --------------------------------------------------------------------------- *
 * Helpers                                                                      *
 * --------------------------------------------------------------------------- */

function useDebounced<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setSettled(value), delayMs);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [value, delayMs]);
  return settled;
}

/**
 * A message that is true about what happened. Never "no briefs found" for a failed call — an empty
 * list and an unreachable server are different facts, and telling a user they have no work when the
 * truth is that we could not ask is the shape of lie this product exists not to tell.
 */
export function messageFor(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401) return "Your session expired. Sign in again to see your briefs.";
    if (error.status === 404)
      return "The brief list isn't switched on for this deployment.";
    return `Couldn't load your briefs (${error.status}). This is a problem reaching the server, not an empty list.`;
  }
  return "Couldn't reach the server to load your briefs. This is not an empty list.";
}
